import { colorUtils, positionUtils } from '../../utils';
import {
  createColorPickerStyleService,
  createFontPickerStyleService,
  TpaStyleService,
  type ResponseColorData,
} from '../../services';

import type { PlatformApiMethods } from '@wix/editor-platform-host-integration-apis';
import type {
  AppData,
  DocumentServicesObject,
} from '@wix/document-services-types';
import type {
  TPAHandler,
  TPAMessage,
  TPAMessageCallback,
} from '../../types/tpaMessage';
import _ from 'lodash';
import { IExperimentsApiAdapter } from '../../../../utils/experimentsApiAdapter';
import { FontPickerOnStyleChangePayload } from '@wix/editor-platform-sdk-types';

export interface EditorUILibHandlers {
  openColorPicker: TPAHandler;
  openFontPicker: TPAHandler;
  openBackgroundPicker: TPAHandler;
}

export type UILibHandlerType = keyof EditorUILibHandlers;

export type ValueFromFontPicker = string | number | boolean;

interface UiLibHandlersServicesDependencies {
  tpaStyleService: TpaStyleService;
}

const defaultColorPickerValue = '#ffffff';

export const createEditorUILibHandlers = (
  platformApiMethods: PlatformApiMethods,
  documentServicesAPI: DocumentServicesObject,
  experimentsAPI: IExperimentsApiAdapter,
  { tpaStyleService }: UiLibHandlersServicesDependencies,
): EditorUILibHandlers => {
  const colorPickerStyleService =
    createColorPickerStyleService(documentServicesAPI);
  const fontPickerStyleService = createFontPickerStyleService(
    documentServicesAPI,
    experimentsAPI,
  );

  const openColorPicker = (msg: TPAMessage, callback: TPAMessageCallback) => {
    const pickerPosition = positionUtils.getTpaSettingsPositionRelativeToEditor(
      {
        top: msg.data?.top,
        left: msg.data?.left,
      },
    );
    const wixParam = msg.data?.wixParam;
    const { compId } = msg;
    const initialValue = msg.data?.color || defaultColorPickerValue;

    const getOnChange = () => {
      if (wixParam) {
        return (newColorData: ResponseColorData) =>
          colorPickerStyleService.onStyleChange(
            compId,
            wixParam,
            newColorData.theme || newColorData.color,
            callback,
            tpaStyleService.setStyleParam,
          );
      }

      return (newColorData: ResponseColorData) =>
        colorPickerStyleService.onStyleChangeWithoutWixParam(
          newColorData,
          callback,
        );
    };

    const onClose = () =>
      callback({
        panelClose: true,
      });

    if (platformApiMethods.editor.selectColor) {
      const onStyleChange = (color: string) => {
        colorPickerStyleService.onStyleChange(
          compId,
          initialValue,
          color,
          callback,
          tpaStyleService.setStyleParam,
        );
      };

      const extractColor = (): string => {
        const isHex =
          colorUtils.isHexColor(initialValue) ||
          colorUtils.isHexaColor(initialValue);
        const isRgb = colorUtils.isRgbColor(initialValue);

        if (isHex || isRgb) {
          return initialValue;
        }

        const compStyle = documentServicesAPI.tpa.style.get(compId);

        if (compStyle.style.colors[initialValue]?.value) {
          return compStyle.style.colors[initialValue]?.value;
        }

        const matchedColor = compStyle.siteColors.find(
          (color) => color.name === initialValue,
        );

        return matchedColor?.value || initialValue;
      };

      platformApiMethods.editor.selectColor({
        initialValue: extractColor(),
        mode: ['solid', 'gradient'],
        onClose,
        onChange: onStyleChange,
        onApply: onStyleChange,
      });

      return;
    }

    platformApiMethods.editor.openColorPicker(
      {} as AppData,
      '',
      {
        overridePosition: true,
        position: pickerPosition,
        color: initialValue,
        onClose,
      },
      getOnChange(),
    );
  };

  const openFontPicker = (msg: TPAMessage, callback: TPAMessageCallback) => {
    const wixParam = msg.data?.wixParam;
    const { compId } = msg;
    const pickerPosition = positionUtils.getTpaSettingsPositionRelativeToEditor(
      {
        top: msg.data?.top,
        left: msg.data?.left,
      },
    );

    const getComponentStyle = () => {
      if (wixParam) {
        const compStyle: Record<string, any> =
          documentServicesAPI.tpa.style.get(compId).style?.fonts[wixParam];
        compStyle.htmlTag = compStyle.htmlTag ?? msg?.data?.htmlTag;
        return compStyle;
      }

      return _.isPlainObject(msg.data?.value) ? msg.data?.value : {};
    };

    const getOnChange = (compStyle: any) => {
      const extractOnChangePayload = (
        styles: any[],
      ): FontPickerOnStyleChangePayload => {
        if (styles.length === 3) {
          return styles as FontPickerOnStyleChangePayload;
        }

        return styles[0];
      };

      if (wixParam) {
        return (...styles: any[]) => {
          const payload = extractOnChangePayload(styles);
          fontPickerStyleService.onStyleChange(
            compId,
            compStyle,
            wixParam,
            payload,
            callback,
            tpaStyleService.setStyleParam,
          );
        };
      }

      return (...styles: any[]) => {
        const payload = extractOnChangePayload(styles);
        fontPickerStyleService.onStyleChangeWithoutWixParam(
          compStyle,
          callback,
          payload,
        );
      };
    };

    const componentStyle = getComponentStyle();
    const onStyleChange = getOnChange(componentStyle);

    const getPanelSectionDefinitionValue = (
      shouldShow?: boolean,
    ): 'enabled' | 'hidden' => (shouldShow ? 'enabled' : 'hidden');

    const options = {
      position: pickerPosition,
      title: msg.data?.title,
      panelSectionsDefinition: {
        theme: getPanelSectionDefinitionValue(!msg.data?.hideTheme),
        font: getPanelSectionDefinitionValue(!msg.data?.hideFont),
        size: getPanelSectionDefinitionValue(!msg.data?.hideSize),
        style: getPanelSectionDefinitionValue(!msg.data?.hideStyle),
        htmlTag: getPanelSectionDefinitionValue(msg.data?.showHTMLTag),
        fontMaxSize: msg.data?.fontMaxSize,
        fontMinSize: msg.data?.fontMinSize,
      },
      componentStyle,
      overridePositon: true,
    };

    platformApiMethods.editor.openFontPicker(
      {} as AppData,
      '',
      options,
      onStyleChange,
    );
  };

  const openBackgroundPicker = (
    msg: TPAMessage,
    callback: TPAMessageCallback,
  ) => {
    const color = msg.data?.color || defaultColorPickerValue;
    const pickerPosition = positionUtils.getTpaSettingsPositionRelativeToEditor(
      {
        top: msg.data?.top,
        left: msg.data?.left,
      },
    );

    const options = {
      color,
      position: pickerPosition,
      onChange: callback,
      onClose: () =>
        callback({
          panelClose: true,
        }),
    };

    platformApiMethods.editor.openBackgroundPicker?.(
      {} as AppData,
      '',
      options,
    );
  };

  return {
    openColorPicker,
    openFontPicker,
    openBackgroundPicker,
  };
};
